import AnimationController from '../../controllers/animation';
// import DetectController from '../../controllers/detect';
import HeightController from '../../controllers/height';
import ResizeController from '../../controllers/resize';

import Overlay from './overlay';

import { getRootOffset, lerp, clamp } from '../../helpers';

export default class Dragger {

	el = {};

	animation;

	options = {
		ease: 0.05,
		speed: 4.5
	};

	flags = {
		isDragging: false
	};

	bounds = {
		offsetLeft: 0,
		totalWidth: 0,
		visibleWidth: 0,
		slideWidth: 0,
		slideHeight: 0
	};

	mousedownX = 0;
	mouseupX = 0;

	currentX = 0;
	lastX = 0;

	currentProgressX = 0;
	lastProgressX = 0;
	touchProgressX = 0;

	min = 0;
	max = 0;

	anchorX = 0;
	height = 0;

	activeIndex = 1;
	total = 0;

	overlay;

	timeouts = {
		mouse: null
	};

	constructor(container) {
		this.el = {
			container,
			wrap: container.querySelector('[data-slider-wrap]'),
			stage: container.querySelector('[data-slider-stage]'),
			list: container.querySelector('ul') || container.querySelector('ol'),
			slides: [...container.querySelectorAll('li')],
			anchors: [...container.querySelectorAll('a')],
			progressBg: container.querySelector('[data-slider-progress-bg]'),
			progressBar: container.querySelector('[data-slider-progress-bar]')
		};

		// this.isDesktop = DetectController.isDesktop;
		this.isDesktop = window.matchMedia('(min-width: 1280px)').matches;
		this.isDesktop ? this.init() : this.initProgress();

		if (this.el.container.classList.contains('slider-team__container')) {
			const overlay = this.el.container.closest('.slider-team').nextElementSibling;
			this.overlay = new Overlay(overlay, this.el.slides);
		}
	}

	initProgress() {
		this.measure();
		this.animation = null;
		this.animation = AnimationController.set({
			update: this.#updateTouch,
			render: this.#renderTouch
		});
	}

	#updateTouch = () => {
		this.touchProgressX = (this.el.stage.scrollLeft / this.bounds.totalWidth) * this.el.progressBg.offsetWidth;
		return true;
	};

	#renderTouch = () => {
		this.el.progressBar.style.transform = `translate3d(${this.touchProgressX}px, 0, 0)`;
	};

	init() {
		this.measure();
		this.setup();
		this.addEventListeners();
	}

	measure() {
		this.bounds.winWidth = window.innerWidth;
		this.bounds.offsetLeft = getRootOffset(this.el.stage).left;
		this.bounds.slideWidth = this.el.slides[0].offsetWidth;
		this.bounds.totalWidth = this.el.slides.length * this.bounds.slideWidth;
		this.bounds.visibleWidth = this.el.wrap.clientWidth;
	}

	setup() {
		this.total = this.el.slides.length - 1;

		const { winWidth, visibleWidth, totalWidth } = this.bounds;
		const offset = winWidth * 0.01;

		for (const slide of this.el.slides) {
			const height = slide.clientHeight;
			if (height > this.height) this.height = height;
		}

		this.el.stage.style.height = `${this.height}px`;
		this.max = -(totalWidth - visibleWidth - offset);
	}

	// LISTENERS

	addEventListeners() {
		ResizeController.add(this.#onResize);
		HeightController.add(this.#onResize);

		this.el.container.addEventListener('mousemove', this.#onMouseMove, { passive: true });
		this.el.container.addEventListener('mousedown', this.#onMouseDown, false);
		this.el.container.addEventListener('mouseup', this.#onMouseUp, false);
		this.el.container.addEventListener('mouseleave', this.#onMouseLeave, false);
		this.el.container.addEventListener('mouseenter', this.#onMouseEnter, false);
	}

	removeEventListeners() {
		ResizeController.remove(this.#onResize);
		HeightController.remove(this.#onResize);

		this.el.container.removeEventListener('mousemove', this.#onMouseMove);
		this.el.container.removeEventListener('mousedown', this.#onMouseDown);
		this.el.container.removeEventListener('mouseup', this.#onMouseUp);
		this.el.container.removeEventListener('mouseleave', this.#onMouseLeave);
		this.el.container.removeEventListener('mouseenter', this.#onMouseEnter);
	}

	// EVENTS

	#onResize = () => {
		this.measure();
		this.setup();
	}

	#onMouseMove = (e) => {
		this.move(e);
	}

	#onMouseDown = (e) => {
		// Prevent firefox dragging images
		e.preventDefault();
		this.drag(e);
	}

	#onMouseUp = () => {
		this.undrag();
	}

	#onMouseLeave = () => {
		this.timeouts.mouse = setTimeout(() => this.disable(), 1500);
	}

	#onMouseEnter = () => {
		this.disable();
		this.start();
	}

	// METHODS

	progress(current, end) {
		this.currentProgressX = (current / end) * (this.el.progressBg.offsetWidth - this.el.progressBar.offsetWidth);
	}

	move(e) {
		if (!this.flags.isDragging) return;
		const userX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
		this.currentX = this.mouseupX + (userX - this.mousedownX) * this.options.speed;
		this.clamp();
		this.progress(this.currentX, this.max);
	}

	drag(e) {
		this.flags.isDragging = true;
		const userX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
		this.mousedownX = userX;
	}

	undrag() {
		this.flags.isDragging = false;
		this.mouseupX = this.currentX;
	}

	// ANIMATIONS

	start() {
		this.animation = AnimationController.set({
			update: this.#update,
			render: this.#render
		});
	}

	stop() {
		AnimationController.clear(this.animation);
	}

	disable() {
		if (this.timeouts.mouse) clearTimeout(this.timeouts.mouse);
		this.undrag();
		this.stop();
	}

	#update = () => {
		this.lastX = lerp(this.lastX, this.currentX, this.options.ease);
		this.lastX = Math.floor(this.lastX * 100) / 100;
		this.lastProgressX = lerp(this.lastProgressX, this.currentProgressX, this.options.ease);
		return true;
	}

	#render = () => {
		this.el.stage.style.transform = `translate3d(${this.lastX}px, 0, 0)`;
		this.el.progressBar.style.transform = `translateX(${this.lastProgressX}px)`;
	}

	clamp() {
		this.currentX = clamp(this.currentX, this.min, this.max);
	}

	// DESTROY EVERYTHING

	clearStyles() {
		this.el.stage.style = '';
	}

	destroy() {
		if (this.timeouts.mouse) clearTimeout(this.timeouts.mouse);
		if (this.overlay) this.overlay.destroy();
		this.clearStyles();
		this.stop();
		this.removeEventListeners();
	}

}
